import { useState, useEffect } from "react";
import type { ReputationProgress, ReviewItem } from "../types";
import { StarRating } from "../StarRating";
import { originalReviewText } from "../review-text";
import { SentimentBadge } from "../RiskBadge";
import type { PlatformApiClient } from "../../api";
import { AcquisitionResultsReport } from "./AcquisitionResultsReport";
import "./review-boost-reports-areas.css";

const REPORT_PERIODS = [
  { key: "7d", label: "Últimos 7 días" },
  { key: "30d", label: "Últimos 30 días" },
  { key: "este_mes", label: "Este mes" },
  { key: "3m", label: "Últimos 3 meses" },
  { key: "custom", label: "Personalizado" },
] as const;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function resolvePeriodDates(key: string, custom?: { from: string; to: string }) {
  const today = new Date();
  const todayStr = localDateStr(today);
  switch (key) {
    case "7d": {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { from: localDateStr(d), to: todayStr };
    }
    case "30d": {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { from: localDateStr(d), to: todayStr };
    }
    case "3m": {
      const d = new Date(today);
      d.setMonth(d.getMonth() - 3);
      d.setDate(d.getDate() + 1);
      return { from: localDateStr(d), to: todayStr };
    }
    case "custom":
      return { from: custom?.from || todayStr, to: custom?.to || todayStr };
    case "este_mes":
    default:
      return { from: `${todayStr.slice(0, 7)}-01`, to: todayStr };
  }
}

function formatRangeLabel(from: string, to: string): string {
  const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  try {
    const [fy, fm, fd] = from.split("-").map(Number);
    const [ty, tm, td] = to.split("-").map(Number);
    if (!fy || !fm || !fd || !ty || !tm || !td) return `${from} – ${to}`;
    if (from === to) return `${fd} ${MONTHS[fm - 1]} ${fy}`;
    if (fy === ty && fm === tm) return `${fd}–${td} ${MONTHS[tm - 1]} ${ty}`;
    if (fy === ty) return `${fd} ${MONTHS[fm - 1]} – ${td} ${MONTHS[tm - 1]} ${ty}`;
    return `${fd} ${MONTHS[fm - 1]} ${fy} – ${td} ${MONTHS[tm - 1]} ${ty}`;
  } catch {
    return `${from} – ${to}`;
  }
}

function formatDelay(hours: number | null): string {
  if (hours == null) return "Sin datos aún";
  if (hours < 1) return "< 1 hora";
  if (hours < 24) return `${Math.round(hours)} horas`;
  const days = (hours / 24).toFixed(1);
  return `${days} días`;
}

function formatAgo(isoString: string): string {
  try {
    const d = new Date(isoString);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return "Hoy";
    if (diffDays === 1) return "Hace 1 día";
    if (diffDays < 30) return `Hace ${diffDays} días`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return "Hace 1 mes";
    return `Hace ${diffMonths} meses`;
  } catch {
    return "Reciente";
  }
}

export function ReportsView({
  initialData,
  initialTopic = null,
  slug,
  api,
  base,
  navigate,
  onExpired,
}: {
  initialData: ReputationProgress | null;
  initialTopic?: string | null;
  slug: string;
  api: PlatformApiClient;
  base: string;
  navigate: (path: string) => void;
  onExpired: () => void;
}) {
  const [reportSection, setReportSection] = useState<"attention" | "acquisition">("attention");
  const [periodKey, setPeriodKey] = useState<string>("este_mes");
  const [customFrom, setCustomFrom] = useState(localDateStr(new Date()));
  const [customTo, setCustomTo] = useState(localDateStr(new Date()));
  const [progress, setProgress] = useState<ReputationProgress | null>(initialData);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Topic Drilldown drawer state
  const [selectedTopic, setSelectedTopic] = useState<string | null>(initialTopic);
  const [topicSentiment, setTopicSentiment] = useState("");
  const [topicReviews, setTopicReviews] = useState<ReviewItem[]>([]);
  const [topicTotal, setTopicTotal] = useState(0);
  const [topicLoading, setTopicLoading] = useState(false);
  const [topicError, setTopicError] = useState<string | null>(null);

  useEffect(() => {
    if (initialTopic !== undefined) {
      setSelectedTopic(initialTopic);
    }
  }, [initialTopic]);

  const range = resolvePeriodDates(periodKey, { from: customFrom, to: customTo });

  useEffect(() => {
    if (reportSection !== "attention") return;
    let active = true;
    const currentRange = resolvePeriodDates(periodKey, { from: customFrom, to: customTo });
    const query = `?from=${encodeURIComponent(currentRange.from)}&to=${encodeURIComponent(currentRange.to)}`;
    setLoading(true);
    setLoadError(null);

    api
      .reputation<{ progress: ReputationProgress }>(slug, `/reputation-progress${query}`)
      .then((res) => {
        if (active) setProgress(res.progress ?? null);
      })
      .catch(() => {
        if (active) {
          setLoadError("No se pudieron actualizar los reportes del período seleccionado. No se muestran cifras anteriores como si fueran actuales.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [api, slug, reportSection, periodKey, customFrom, customTo]);

  // Keep the topic drawer accessible and return focus to its trigger on close.
  useEffect(() => {
    if (!selectedTopic) return;
    const previous = document.activeElement as HTMLElement | null;
    const dialog = document.querySelector<HTMLElement>(".rep-topic-drawer");
    if (!dialog) return;
    const controls = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex="0"]'
    ));
    controls()[0]?.focus();
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedTopic(null);
        return;
      }
      if (event.key !== "Tab") return;
      const items = controls();
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    dialog.addEventListener("keydown", handleKeydown);
    return () => {
      dialog.removeEventListener("keydown", handleKeydown);
      if (previous?.isConnected) previous.focus();
    };
  }, [selectedTopic]);

  // Load topic reviews when topic is opened
  useEffect(() => {
    if (!selectedTopic) return;
    let active = true;
    setTopicLoading(true);
    setTopicError(null);
    setTopicReviews([]);
    const params = new URLSearchParams({
      from: range.from,
      to: range.to,
      limit: "20",
    });
    if (topicSentiment) params.set("sentiment", topicSentiment);

    api
      .reputation<{ reviews: ReviewItem[]; total: number }>(
        slug,
        `/reports/topics/${encodeURIComponent(selectedTopic)}/reviews?${params}`
      )
      .then((res) => {
        if (active) {
          setTopicReviews(res.reviews || []);
          setTopicTotal(res.total || 0);
        }
      })
      .catch(() => {
        if (active) {
          setTopicReviews([]);
          setTopicError("No se pudieron consultar las reseñas de este tema. Probá cerrando y abriendo nuevamente el detalle.");
        }
      })
      .finally(() => {
        if (active) setTopicLoading(false);
      });

    return () => {
      active = false;
    };
  }, [api, slug, selectedTopic, topicSentiment, range.from, range.to]);

  const p = progress;
  const pendingCount = p ? Math.max(0, p.newReviewsCurrent - p.respondedCount) : 0;

  // SVG Line Chart coordinates calculation
  const evolution = p?.ratingEvolution?.current || [];
  const hasChartData = evolution.some((pt) => pt.count > 0);

  const w = 680;
  const h = 220;
  const pad = 40;
  const padBottom = 40;
  const n = evolution.length;

  const xAt = (i: number) => (n > 1 ? pad + (i * (w - pad * 2)) / (n - 1) : w / 2);
  const yAt = (v: number) => h - padBottom - ((v - 1) / 4) * (h - pad - padBottom);

  const coords = evolution
    .map((pt, i) => (pt.avg != null ? { x: xAt(i), y: yAt(pt.avg), pt } : null))
    .filter((c): c is { x: number; y: number; pt: (typeof evolution)[0] } => c !== null);

  const pathD = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ");
  const areaD = coords.length
    ? `${pathD} L ${coords[coords.length - 1]!.x} ${h - padBottom} L ${coords[0]!.x} ${h - padBottom} Z`
    : "";
  const ratingValues = coords
    .map((point) => point.pt.avg)
    .filter((value): value is number => value != null);
  const ratingIsFlat = ratingValues.length > 1
    && Math.max(...ratingValues) - Math.min(...ratingValues) < 0.01;
  const flatRating = ratingValues[0] ?? p?.avgRatingCurrent ?? null;

  return (
    <div className="rep-reports-view rep-layout-approved" data-layout="RB-RPT-B">
      <header className="page-heading">
        <p className="eyebrow">Rendimiento y análisis</p>
        <h1>Reportes de Review Boost</h1>
        <p>Analizá por separado la atención de reseñas y la captación de nuevas opiniones, sin confundir solicitudes con resultados verificados.</p>
      </header>

      <div className="rep-report-domain-nav" role="group" aria-label="Áreas de reportes">
        <button type="button" aria-pressed={reportSection === "attention"}
          onClick={() => setReportSection("attention")}>
          <strong>Atención de reseñas</strong>
          <span>Calificaciones, respuestas y temas</span>
        </button>
        <button type="button" aria-pressed={reportSection === "acquisition"}
          onClick={() => { setSelectedTopic(null); setReportSection("acquisition"); }}>
          <strong>Captación de reseñas</strong>
          <span>Invitaciones y disponibilidad de evidencia</span>
        </button>
      </div>

      {reportSection === "acquisition" && (
        <AcquisitionResultsReport slug={slug} api={api} base={base}
          navigate={navigate} onExpired={onExpired} />
      )}

      {reportSection === "attention" && (<>
      {/* Period Filter Toolbar */}
      <section className="details-card rep-report-toolbar" aria-label="Filtro de período">
        <div className="rep-toolbar-row">
          <div className="rep-period-select-wrap">
            <label htmlFor="rep-period-select">Período:</label>
            <select
              id="rep-period-select"
              value={periodKey}
              onChange={(e) => setPeriodKey(e.target.value)}
            >
              {REPORT_PERIODS.map((period) => (
                <option key={period.key} value={period.key}>{period.label}</option>
              ))}
            </select>
          </div>

          <span className="rep-range-badge">{formatRangeLabel(range.from, range.to)}</span>

          {loading && p && (
            <span className="rep-report-refresh-status" role="status">Actualizando datos…</span>
          )}

          <div className="rep-toolbar-actions">
            <button
              type="button"
              className="rep-inline-button"
              onClick={() => window.print()}
            >
              Imprimir / PDF
            </button>
          </div>
        </div>

        {periodKey === "custom" && (
          <div className="rep-custom-dates-row">
            <div className="field">
              <label htmlFor="custom-from">Desde</label>
              <input
                id="custom-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="custom-to">Hasta</label>
              <input
                id="custom-to"
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      {loading ? (
        <div className="details-card empty-state" role="status">
          <p>Actualizando los datos del período seleccionado…</p>
        </div>
      ) : loadError ? (
        <div className="inline-alert" role="alert">
          <p>{loadError}</p>
          <p>Elegí otro período o volvé a seleccionar el actual para intentar de nuevo.</p>
        </div>
      ) : !p ? (
        <div className="details-card empty-state">
          <p>No se encontraron datos para el período seleccionado.</p>
        </div>
      ) : (
        <>
          <section className="rep-report-attention-strip" aria-label="Prioridad del período">
            <div>
              <span className="rep-command-kicker">Lectura operativa</span>
              <strong>
                {p.oldestPending
                  ? `Hay una reseña pendiente hace ${p.oldestPending.daysPending} ${p.oldestPending.daysPending === 1 ? "día" : "días"}`
                  : pendingCount > 0
                    ? `${pendingCount} ${pendingCount === 1 ? "reseña sigue" : "reseñas siguen"} sin respuesta en el período`
                    : "No quedan reseñas sin responder en este período"}
              </strong>
              <span>
                Cobertura de respuesta: {p.responseRatePct}% · {p.negativeCount} {p.negativeCount === 1 ? "reseña negativa" : "reseñas negativas"}.
              </span>
            </div>
            {p.oldestPending && (
              <button
                type="button"
                className="primary-button"
                onClick={() => navigate(`${base}/reviews/${encodeURIComponent(p.oldestPending!.id)}`)}
              >
                Atender reseña pendiente
              </button>
            )}
          </section>

          {/* KPI Summary Grid */}
          <div className="rep-kpi-grid">
            <div className="rep-kpi-card">
              <div className="rep-kpi-icon rep-kpi-icon-rating orange" aria-hidden="true" />
              <div className="rep-kpi-body">
                <span className="rep-kpi-title">Nota del negocio</span>
                <strong className="rep-kpi-value">
                  {p.avgRatingCurrent != null ? `${p.avgRatingCurrent.toFixed(1)} ★` : "—"}
                </strong>
                <span className="rep-kpi-sub">Promedio del período</span>
              </div>
            </div>

            <div className="rep-kpi-card">
              <div className="rep-kpi-icon rep-kpi-icon-reviews blue" aria-hidden="true" />
              <div className="rep-kpi-body">
                <span className="rep-kpi-title">Reseñas recibidas</span>
                <strong className="rep-kpi-value">{p.newReviewsCurrent}</strong>
                <span className="rep-kpi-sub">En este período</span>
              </div>
            </div>

            <div className="rep-kpi-card">
              <div className="rep-kpi-icon rep-kpi-icon-positive green" aria-hidden="true" />
              <div className="rep-kpi-body">
                <span className="rep-kpi-title">Positivas (≥ 4★)</span>
                <strong className="rep-kpi-value">{p.positiveCount}</strong>
                <span className="rep-kpi-sub">
                  {p.newReviewsCurrent > 0
                    ? `${Math.round((p.positiveCount / p.newReviewsCurrent) * 100)}% del total`
                    : "Sin reseñas"}
                </span>
              </div>
            </div>

            <div className="rep-kpi-card">
              <div
                className={`rep-kpi-icon ${p.negativeCount > 0 ? "rep-kpi-icon-attention red" : "rep-kpi-icon-positive green"}`}
                aria-hidden="true"
              />
              <div className="rep-kpi-body">
                <span className="rep-kpi-title">Negativas (&lt; 4★)</span>
                <strong className="rep-kpi-value">{p.negativeCount}</strong>
                <span className="rep-kpi-sub">
                  {p.negativeCount > 0 ? "Atención requerida" : "Sin negativas"}
                </span>
              </div>
            </div>
          </div>

          {/* Charts & Distribution */}
          <div className="rep-split-grid">
            {/* SVG Rating Evolution Chart */}
            <section className="details-card rep-chart-card" aria-label="Evolución del rating">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Tendencia</p>
                  <h2>Evolución del rating promedio</h2>
                </div>
              </div>

              {hasChartData ? (
                ratingIsFlat ? (
                  <div className="rep-flat-rating-state" role="status">
                    <span className="rep-command-kicker">Rating estable</span>
                    <strong>{flatRating != null ? flatRating.toFixed(1) : "—"} ★</strong>
                    <p>La calificación se mantuvo estable durante todo el período seleccionado.</p>
                  </div>
                ) : (
                <div className="rep-chart-container" role="region" aria-label="Gráfico de evolución del rating desplazable" tabIndex={0}>
                  <svg
                    className="rep-svg-chart"
                    viewBox={`0 0 ${w} ${h}`}
                    role="img"
                    aria-label="Gráfico de evolución del rating promedio"
                  >
                    <defs>
                      <linearGradient id="repRatingGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop className="rep-chart-area-start" offset="0%" />
                        <stop className="rep-chart-area-end" offset="100%" />
                      </linearGradient>
                    </defs>

                    {/* Grid lines */}
                    <g className="rep-chart-grid-lines" strokeDasharray="3 3">
                      <line x1={pad} y1={pad} x2={w - pad} y2={pad} />
                      <line x1={pad} y1={yAt(3)} x2={w - pad} y2={yAt(3)} />
                      <line x1={pad} y1={h - padBottom} x2={w - pad} y2={h - padBottom} />
                    </g>

                    {/* Y labels */}
                    <g className="rep-chart-y-labels" fontSize="11">
                      <text x="8" y={pad + 4}>5.0★</text>
                      <text x="8" y={yAt(3) + 4}>3.0★</text>
                      <text x="8" y={h - padBottom + 4}>1.0★</text>
                    </g>

                    {/* Area fill */}
                    {areaD && <path d={areaD} fill="url(#repRatingGrad)" />}

                    {/* Line path */}
                    {pathD && <path className="rep-chart-line" d={pathD} fill="none" strokeWidth="2.5" />}

                    {/* Data dots */}
                    {coords.map((c, idx) => (
                      <circle
                        key={idx}
                        cx={c.x}
                        cy={c.y}
                        r={idx === coords.length - 1 ? 5 : 3.5}
                        className="rep-chart-dot"
                        strokeWidth="2"
                      >
                        <title>{`${c.pt.label}: ${c.pt.avg}★ (${c.pt.count} reseñas)`}</title>
                      </circle>
                    ))}

                    {/* X labels */}
                    <g className="rep-chart-x-labels" fontSize="10.5" textAnchor="middle">
                      {evolution.map((pt, i) => (
                        <text key={i} x={xAt(i)} y={h - padBottom + 20}>
                          {pt.label}
                        </text>
                      ))}
                    </g>
                  </svg>
                  <p className="rep-chart-caption">Cada punto representa el promedio ponderado del tramo.</p>
                </div>
                )
              ) : (
                <div className="empty-state">
                  <p>Todavía no hay suficientes reseñas en este período para graficar.</p>
                </div>
              )}
            </section>

            {/* Star Distribution */}
            <section className="details-card rep-dist-card" aria-label="Distribución de estrellas">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Composición</p>
                  <h2>Distribución de estrellas</h2>
                </div>
              </div>

              <div className="rep-star-dist-list">
                {p.starDistribution?.map((item) => (
                  <div key={item.stars} className="rep-dist-row">
                    <span className="rep-dist-stars">{item.stars} ★</span>
                    <div
                      className="rep-dist-track"
                      role="progressbar"
                      aria-label={`${item.stars} estrellas`}
                      aria-valuenow={item.pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuetext={`${item.pct}% (${item.count} reseñas)`}
                    >
                      <div className="rep-dist-fill" style={{ width: `${item.pct}%` }} />
                    </div>
                    <span className="rep-dist-count">{item.pct}% ({item.count})</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Operational Metrics */}
          <div className="rep-split-grid">
            <section className="details-card rep-metric-box">
              <p className="eyebrow">Tasa de respuesta</p>
              <h2>Cobertura de atención</h2>
              <strong className="rep-big-metric">{p.responseRatePct}%</strong>
              <p className="rep-metric-note">
                {p.respondedCount} de {p.newReviewsCurrent} reseñas respondidas en este período.
              </p>
            </section>

            <section className="details-card rep-metric-box">
              <p className="eyebrow">Velocidad</p>
              <h2>Demora media de respuesta</h2>
              <strong className="rep-big-metric">{formatDelay(p.avgResponseDelayHours)}</strong>
              <p className="rep-metric-note">
                Tiempo promedio transcurrido desde la publicación hasta la respuesta.
              </p>
            </section>
          </div>

          {/* Frequent Topics */}
          <section className="details-card rep-topics-section" aria-label="Temas frecuentes">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Menciones destacadas</p>
                <h2>Temas frecuentes</h2>
              </div>
            </div>
            <p className="rep-section-desc">
              Hacé clic en cualquier tema para ver las reseñas de clientes relacionadas.
            </p>

            <div className="rep-topic-groups">
              {/* Positive topics */}
              <div className="rep-topic-group">
                <span className="rep-topic-group-title positive">● Positivo</span>
                <div className="rep-topic-pill-list">
                  {p.topics?.positivo?.length ? (
                    p.topics.positivo.map((t) => (
                      <button
                        key={t.label}
                        type="button"
                        className="rep-topic-pill positive"
                        onClick={() => setSelectedTopic(t.label)}
                      >
                        <span>{t.label}</span>
                        <b>{t.count}</b>
                      </button>
                    ))
                  ) : (
                    <span className="rep-empty-pill">Sin menciones</span>
                  )}
                </div>
              </div>

              {/* Neutral topics */}
              <div className="rep-topic-group">
                <span className="rep-topic-group-title neutral">● Neutro</span>
                <div className="rep-topic-pill-list">
                  {p.topics?.neutral?.length ? (
                    p.topics.neutral.map((t) => (
                      <button
                        key={t.label}
                        type="button"
                        className="rep-topic-pill neutral"
                        onClick={() => setSelectedTopic(t.label)}
                      >
                        <span>{t.label}</span>
                        <b>{t.count}</b>
                      </button>
                    ))
                  ) : (
                    <span className="rep-empty-pill">Sin menciones</span>
                  )}
                </div>
              </div>

              {/* Negative topics */}
              <div className="rep-topic-group">
                <span className="rep-topic-group-title negative">● Negativo</span>
                <div className="rep-topic-pill-list">
                  {p.topics?.negativo?.length ? (
                    p.topics.negativo.map((t) => (
                      <button
                        key={t.label}
                        type="button"
                        className="rep-topic-pill negative"
                        onClick={() => setSelectedTopic(t.label)}
                      >
                        <span>{t.label}</span>
                        <b>{t.count}</b>
                      </button>
                    ))
                  ) : (
                    <span className="rep-empty-pill">Sin menciones</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Oldest Pending Action Card */}
          {p.oldestPending && (
            <section className="details-card rep-oldest-pending-card" aria-label="Reseña pendiente más antigua">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Requiere atención</p>
                  <h2>Reseña pendiente más antigua</h2>
                </div>
                <span className="rep-status-pill warn">
                  {p.oldestPending.daysPending === 0 ? "Menos de un día sin responder" : `${p.oldestPending.daysPending} ${p.oldestPending.daysPending === 1 ? "día" : "días"} sin responder`}
                </span>
              </div>
              <div className="rep-oldest-pending-body">
                <div>
                  <StarRating rating={p.oldestPending.rating} size="sm" />
                  <p className="rep-review-snippet">
                    {originalReviewText(p.oldestPending.text) || "Sin comentario de texto."}
                  </p>
                  <span className="rep-author-name">{p.oldestPending.author}</span>
                </div>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => navigate(`${base}/reviews/${encodeURIComponent(p.oldestPending!.id)}`)}
                >
                  Ver reseña →
                </button>
              </div>
            </section>
          )}
        </>
      )}

      {/* Topic Drilldown Drawer / Modal */}
      {selectedTopic && (
        <div className="rep-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="topic-modal-title">
          <div className="rep-modal-card rep-topic-drawer">
            <div className="rep-modal-head">
              <div>
                <p className="eyebrow">Drilldown de temas</p>
                <h2 id="topic-modal-title">Reseñas sobre "{selectedTopic}"</h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTopic(null)}
                aria-label="Cerrar ventana de temas"
              >
                ×
              </button>
            </div>

            <div className="rep-topic-filter-row">
              <label htmlFor="topic-sentiment">Filtrar por sentimiento:</label>
              <select
                id="topic-sentiment"
                value={topicSentiment}
                onChange={(e) => setTopicSentiment(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="positivo">Positivo</option>
                <option value="neutral">Neutro</option>
                <option value="negativo">Negativo</option>
              </select>
              <span className="rep-topic-count-note">{topicTotal} reseñas encontradas</span>
            </div>

            <div className="rep-topic-review-list">
              {topicLoading ? (
                <div className="empty-state"><p>Cargando reseñas…</p></div>
              ) : topicError ? (
                <div className="inline-alert" role="alert"><p>{topicError}</p></div>
              ) : topicReviews.length === 0 ? (
                <div className="empty-state"><p>No se encontraron reseñas para este tema.</p></div>
              ) : (
                topicReviews.map((r) => (
                  <div key={r.id} className="rep-topic-review-item">
                    <div className="rep-topic-review-header">
                      <strong>{r.author}</strong>
                      <div className="rep-author-meta">
                        <StarRating rating={r.rating} size="sm" />
                        <SentimentBadge sentiment={r.sentiment} />
                        <span>{formatAgo(r.published_at)}</span>
                      </div>
                    </div>
                    <p className="rep-topic-review-text">{originalReviewText(r.text) || "Sin comentario escrito."}</p>
                    <button
                      type="button"
                      className="rep-inline-button"
                      onClick={() => {
                        setSelectedTopic(null);
                        navigate(`${base}/reviews/${encodeURIComponent(r.id)}`);
                      }}
                    >
                      Ver en Bandeja de Reseñas →
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}